//PROPERWORKING CODE

import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import "./GalleryUploadCard.css";
import {
  startUploadAPI,
  completeUploadAPI,
  getBookingByIdAPI,
  getPartUploadUrlAPI
} from "../../../../utils/APIs/bookingsApis";
import axios from "axios";

import { toast } from "react-toastify";
import Loader from "../../../../Loader/Loader";
import { FaEye } from "react-icons/fa";
import Loader2 from "../../../../Template/Loader/Loader2";

const GalleryAddPhotos = ({ showHeader = true, showBookingInfo = true }) => {

  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [bookingData, setBookingData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(() => {
    return localStorage.getItem(`is_uploading_${bookingId}`) === "true";
  });
  const [uploadProgress, setUploadProgress] = useState(() => {
    const saved = localStorage.getItem(`upload_progress_${bookingId}`);
    return saved ? parseInt(saved) : 0;
  });
  const uploadedBytesRef = useRef(0);
  const totalBytesRef = useRef(0);
  const activeUploadsRef = useRef({}); // Track bytes in-progress per worker
  const [isPaused, setIsPaused] = useState(() => {
    return localStorage.getItem(`is_paused_${bookingId}`) === "true";
  });
  const [isRetrying, setIsRetrying] = useState(false);
  const pauseRef = useRef(false);
  const cancelRef = useRef(false);
  // const [isCancelled, setIsCancelled] = useState(false);
  const [statusText, setStatusText] = useState("");

  // Sync states to local storage
  useEffect(() => {
    localStorage.setItem(`is_uploading_${bookingId}`, uploading.toString());
  }, [uploading, bookingId]);

  useEffect(() => {
    localStorage.setItem(`is_paused_${bookingId}`, isPaused.toString());
    pauseRef.current = isPaused;
  }, [isPaused, bookingId]);

  // Helper to check pause state from storage (cross-instance safe)
  const isCurrentlyPaused = () => {
    return localStorage.getItem(`is_paused_${bookingId}`) === "true" || isPaused;
  }

  // Internet connectivity handlers
  useEffect(() => {
    const handleOffline = () => {
      setIsPaused(true);
      toast.error("Lost Internet Connection! Upload Paused.", {
        position: "top-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: "offline-toast"
      });
    };

    const handleOnline = () => {
      toast.dismiss("offline-toast");
      toast.success("Internet Restored! You can now Resume.", {
        position: "top-center",
        autoClose: 5000,
      });
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  // Helper to wait for internet
  const waitForConnectivity = async () => {
    while (!navigator.onLine || isCurrentlyPaused()) {
      if (cancelRef.current) break;
      if (!navigator.onLine) setIsRetrying(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    setIsRetrying(false);
  };

  const handleCancel = () => {
    if (window.confirm("Are you sure you want to cancel the entire upload? Progress will be lost.")) {
       cancelRef.current = true;
      //  setIsCancelled(true);
       setUploading(false);
       localStorage.setItem(`is_uploading_${bookingId}`, "false");
       localStorage.removeItem(`is_uploading_${bookingId}`);
       localStorage.removeItem(`is_paused_${bookingId}`);
       localStorage.removeItem(`upload_progress_${bookingId}`);
       toast.info("Upload cancelled.");
    }
  }

  // SYNC UI WITH BACKGROUND PROCESS
  useEffect(() => {
    if (uploading) {
      const interval = setInterval(() => {
        const stillUploading = localStorage.getItem(`is_uploading_${bookingId}`);
        if (stillUploading === "false") {
          setUploading(false);
          return;
        }

        const savedProgress = localStorage.getItem(`upload_progress_${bookingId}`);
        if (savedProgress) {
          const val = parseInt(savedProgress);
          // Only sync UPWARDS and only if we aren't at the very start (avoiding race with 0 reset)
          setUploadProgress(prev => (val > prev && val > 0) ? val : prev);
        }
        
        const isPausedStorage = localStorage.getItem(`is_paused_${bookingId}`) === "true";
        if (isPausedStorage !== isPaused) {
          setIsPaused(isPausedStorage);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [uploading, bookingId, isPaused]);

  const handleBack = () => {
     if (uploading) {
    toast.warning("Upload in progress. Please wait...");
    return;
  }
    navigate(-1);
  }

  /* ================= FETCH BOOKING ================= */

  useEffect(() => {

    const fetchBookingById = async () => {
      try {

        setLoading(true);

        const res = await getBookingByIdAPI(bookingId);

        if (res?.data?.success) {
          setBookingData(res?.data?.data);
        }

      } catch {
        toast.error("Failed to fetch booking details");
      } finally {
        setLoading(false);
      }
    };

    if (bookingId) fetchBookingById();

  }, [bookingId]);

  // RESTORE PROGRESS ON MOUNT
  useEffect(() => {
    // If we remount and were uploading, try to resume visual progress
    if (uploading && uploadProgress === 0) {
      const savedProgress = localStorage.getItem(`upload_progress_${bookingId}`);
      if (savedProgress) setUploadProgress(parseInt(savedProgress));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, uploading]);

  // Update progress in local storage
  useEffect(() => {
    if (uploadProgress > 0) {
      localStorage.setItem(`upload_progress_${bookingId}`, uploadProgress.toString());
    }
  }, [uploadProgress, bookingId]);

 /* ================= Prevent page refresh during upload ================= */

  useEffect(() => {
  const handleBeforeUnload = (e) => {
    if (uploading) {
      e.preventDefault();
      e.returnValue = "";
    }
  };

  window.addEventListener("beforeunload", handleBeforeUnload);

  return () => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };
}, [uploading]);

  /* ================= FILE SELECT ================= */

  const handleFileSelect = (e) => {
    setFiles(Array.from(e.target.files));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setFiles(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  /* ================= FILE UPLOAD ================= */

  const handleSubmitUpload = async () => {

    if (!files.length) {
      toast.error("Please select files");
      return;
    }

    try {
      console.log("Starting Upload sequence...");
      cancelRef.current = false;
      // setIsCancelled(false);
      setUploading(true);
      localStorage.setItem(`is_uploading_${bookingId}`, "true");
      
      setUploadProgress(0);
      localStorage.setItem(`upload_progress_${bookingId}`, "0");
      
      uploadedBytesRef.current = 0;
      activeUploadsRef.current = {};
      setIsPaused(false);
      setIsRetrying(false);

      const CHUNK_SIZE = 20 * 1024 * 1024; // 20MB chunks for better speed/stability
      let totalBytes = 0;
      files.forEach(f => totalBytes += f.size);
      totalBytesRef.current = totalBytes;
      
      const FILE_CONCURRENCY = 2; // Process 2 files at once (more stable)
      let currentFileIndex = 0;
      
      const updateProgress = () => {
        if (!totalBytesRef.current) return;
        const totalActive = Object.values(activeUploadsRef.current).reduce((a, b) => a + b, 0);
        const totalCalculated = uploadedBytesRef.current + totalActive;
        const percent = Math.round((totalCalculated / totalBytesRef.current) * 100);
        const finalPercent = Math.min(percent, 100);
        
        setUploadProgress(finalPercent);
        localStorage.setItem(`upload_progress_${bookingId}`, finalPercent.toString());
      };
      
      updateProgress(); // Initial 0%

      const isNetworkError = (error) => {
        return !error.response || error.code === 'ERR_NETWORK' || error.message?.includes('NETWORK_CHANGED') || error.message?.includes('Network Error');
      };

      const fileWorker = async () => {
        while (currentFileIndex < files.length && !cancelRef.current) {
          const file = files[currentFileIndex++];
          const relativePath = file.webkitRelativePath || file.name;
          const fileId = `${file.name}-${file.size}-${file.lastModified}`;
          setStatusText(`Preparing ${currentFileIndex} of ${files.length}...`);
          console.log(`Starting File Worker for: ${file.name} (FileID: ${fileId})`);

          let startRes;
          let startSuccess = false;
          while (!startSuccess && !cancelRef.current) {
            try {
              if (!bookingData?.veroaBookingId) {
                throw new Error("Booking data not ready. Please wait.");
              }
              await waitForConnectivity();
              if (cancelRef.current) break;
              setStatusText(`Initializing ${file.name}...`);
              startRes = await startUploadAPI({
                fileName: file.name,
                fileType: file.type,
                relativePath,
                veroaBookingId: bookingData.veroaBookingId,
                fileSize: file.size,
              });
              if (startRes?.data) startSuccess = true;
            } catch (error) {
              console.error(`Start upload failed for ${file.name}:`, error);
              if (isNetworkError(error)) {
                 // Auto-retry briefly for network blips
                 await new Promise(r => setTimeout(r, 2000));
                 continue; 
              }
              setIsPaused(true);
              toast.error(`Connection error on ${file.name}: ${error.message || 'Unknown error'}`);
              await waitForConnectivity();
            }
          }
          if (cancelRef.current) break;

          const { key, uploadId, strategy, uploadUrl } = startRes.data;

          if (strategy === "single") {
            let uploadSuccess = false;
            let retryCount = 0;
            const MAX_RETRIES = 3;

            while (!uploadSuccess && !cancelRef.current) {
              try {
                while (isCurrentlyPaused() && !cancelRef.current) {
                  await new Promise(resolve => setTimeout(resolve, 1000));
                }
                if (cancelRef.current) break;

                await axios.put(uploadUrl, file, {
                  headers: { "Content-Type": file.type || "application/octet-stream" },
                  timeout: 600000, // 10 minutes
                  onUploadProgress: (progressEvent) => {
                    activeUploadsRef.current[`file_${fileId}`] = progressEvent.loaded;
                    updateProgress();
                  }
                });
                delete activeUploadsRef.current[`file_${fileId}`];
                uploadedBytesRef.current += file.size;
                updateProgress();
                uploadSuccess = true;
              } catch (error) {
                if (isNetworkError(error) && retryCount < MAX_RETRIES) {
                    retryCount++;
                    // eslint-disable-next-line
                    await new Promise(r => setTimeout(r, 1000 * retryCount));
                    continue;
                }
                retryCount++;
                console.error(`Single upload failed for ${file.name} attempt ${retryCount}:`, error);
                
                if (retryCount >= MAX_RETRIES || !navigator.onLine) {
                  delete activeUploadsRef.current[`file_${file.name}`];
                  setIsPaused(true);
                  toast.error(`File ${file.name} failed after ${MAX_RETRIES} tries. Paused.`);
                  await waitForConnectivity();
                  retryCount = 0;
                } else {
                  // eslint-disable-next-line
                   await new Promise(r => setTimeout(r, 1000 * retryCount));
                }
              }
            }
            if (cancelRef.current) break;

            let completeSuccess = false;
            while (!completeSuccess && !cancelRef.current) {
              try {
                await waitForConnectivity();
                if (cancelRef.current) break;
                await completeUploadAPI({
                  key,
                  uploadId: null,
                  parts: [],
                  bookingid: bookingId,
                  clientId: bookingData.client_id?._id,
                  photographerId: bookingData.photographer_id,
                  veroaBookingId: bookingData.veroaBookingId,
                });
                completeSuccess = true;
              } catch (error) {
                console.error(`Complete upload failed for ${file.name}:`, error);
                setIsPaused(true);
                await waitForConnectivity();
              }
            }
            if (cancelRef.current) break;

            const finalPercent = Math.round((uploadedBytesRef.current / totalBytesRef.current) * 100);
            setUploadProgress(Math.min(finalPercent, 100));
            localStorage.setItem(`upload_progress_${bookingId}`, Math.min(finalPercent, 100).toString());

          } else {
            const totalParts = Math.ceil(file.size / CHUNK_SIZE);
            let parts = new Array(totalParts); 

            const CHUNK_CONCURRENCY = 3; // 3 chunks per file
            let currentPartIndex = 0;
            const partNumbers = Array.from({ length: totalParts }, (_, i) => i + 1);

            const chunkWorker = async () => {
               let retryCount = 0;
              while (currentPartIndex < partNumbers.length && !cancelRef.current) {
                const partNumber = partNumbers[currentPartIndex++];
                const start = (partNumber - 1) * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunkFile = file.slice(start, end);

                let uploadSuccess = false;
                // let retryCount = 0;
                const MAX_RETRIES = 3;

                while (!uploadSuccess && !cancelRef.current) {
                  try {
                    await waitForConnectivity();
                    if (cancelRef.current) break;

                    const urlRes = await getPartUploadUrlAPI({
                      key,
                      uploadId,
                      partNumber: partNumber.toString()
                    });

                    const chunkRes = await axios.put(urlRes.data.uploadUrl, chunkFile, {
                      headers: { "Content-Type": file.type || "application/octet-stream" },
                      timeout: 600000, 
                      onUploadProgress: (progressEvent) => {
                        activeUploadsRef.current[`chunk_${fileId}_${partNumber}`] = progressEvent.loaded;
                        updateProgress();
                      }
                    });

                    parts[partNumber - 1] = {
                      ETag: chunkRes.headers.etag || chunkRes.headers.ETag || chunkRes.headers['etag'],
                      PartNumber: partNumber,
                    };

                    delete activeUploadsRef.current[`chunk_${fileId}_${partNumber}`];
                    uploadedBytesRef.current += chunkFile.size;
                    updateProgress();
                    uploadSuccess = true;

                  } catch (error) {
                    retryCount++;
                    console.error(`Chunk ${partNumber} attempt ${retryCount} failed:`, error.message);
                    
                    if (retryCount >= MAX_RETRIES || !navigator.onLine) {
                      delete activeUploadsRef.current[`chunk_${file.name}_${partNumber}`];
                      if (cancelRef.current) break;
                      setIsRetrying(true);
                      setIsPaused(true);
                      toast.error(`Chunk ${partNumber} failed after ${MAX_RETRIES} tries. Paused.`);
                      await waitForConnectivity();
                      retryCount = 0; // Reset for after resume
                    } else {
                      // Small wait before local retry
                      // eslint-disable-next-line
                      await new Promise(r => setTimeout(r, 1000 * retryCount));
                    }
                  }
                }
              }
            };

            const chunkWorkers = [];
            for (let i = 0; i < Math.min(CHUNK_CONCURRENCY, totalParts); i++) {
              chunkWorkers.push(chunkWorker());
            }
            await Promise.all(chunkWorkers);

            if (cancelRef.current) break;

            let multipartCompleteSuccess = false;
            while (!multipartCompleteSuccess && !cancelRef.current) {
              try {
                await waitForConnectivity();
                if (cancelRef.current) break;
                await completeUploadAPI({
                  key,
                  uploadId,
                  parts: [],
                  bookingid: bookingId,
                  clientId: bookingData.client_id?._id,
                  photographerId: bookingData.photographer_id,
                  veroaBookingId: bookingData.veroaBookingId,
                });
                multipartCompleteSuccess = true;
              } catch (error) {
                console.error(`Multipart complete failed for ${file.name}:`, error);
                if (isNetworkError(error)) {
                    await new Promise(r => setTimeout(r, 3000));
                    continue;
                }
                setIsPaused(true);
                setIsRetrying(true);
                await waitForConnectivity();
              }
            }
          }
        }
      };

      const fileWorkers = [];
      for (let i = 0; i < Math.min(FILE_CONCURRENCY, files.length); i++) {
        fileWorkers.push(fileWorker());
        // Small delay between file starts to prevent API slamming
        await new Promise(r => setTimeout(r, 200));
      }
      await Promise.all(fileWorkers);

      if (!cancelRef.current) {
        setUploading(false); // PROPER EXIT POINT
        localStorage.setItem(`is_uploading_${bookingId}`, "false");
        localStorage.removeItem(`is_uploading_${bookingId}`);
        localStorage.removeItem(`is_paused_${bookingId}`);
        localStorage.removeItem(`upload_progress_${bookingId}`);

        toast.success("All files uploaded successfully 🎉");
        setFiles([]);
        setUploadProgress(100);
      }

    } catch (error) {
      console.error("CRITICAL UPLOAD ERROR:", error);
      const isRecoverable = !navigator.onLine || 
                            (error.message && error.message.includes("Network")) ||
                            (error.code === "ERR_NETWORK");
      
      if (!isRecoverable) {
        toast.error("Upload failed completely. Please try again.");
        setUploading(false);
      } else {
        setIsPaused(true);
        toast.warning("Upload paused due to network error.");
      }
    } finally {
      if (!pauseRef.current && navigator.onLine) {
         // setUploading(false);
      }
    }
  };

  /* ================= VIEW GALLERY ================= */

  const handleViewPictures = () => {
    console.log("BOOKING DATA client id",bookingData?.client_id?._id)
    navigate(`/bookings/view-gallery/${bookingId}/${bookingData?.photographer_id?._id}/${bookingData?.client_id?._id}`);
  };
  /* ================= LOADER ================= */

  if (uploading) {
    return (
      <div style={{ position: 'relative' }}>
        <Loader2 
          percentage={uploadProgress} 
          isPaused={isPaused} 
          statusText={statusText || (isRetrying ? "Reconnecting..." : (isPaused ? "Paused" : "Uploading..."))} 
        />
        <div style={{
          position: 'fixed',
          top: '65%',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10000,
          display: 'flex',
          gap: '10px'
        }}>
          <button 
            className="upload-btn" 
            style={{ 
              background: isPaused ? "#28a745" : "#ffc107", 
              color: "#fff",
              minWidth: "120px" 
            }}
            onClick={() => setIsPaused(!isPaused)}
          >
            {isPaused ? "▶ Resume" : "⏸ Pause"}
          </button>
          <button 
            className="upload-btn" 
            style={{ 
              background: "#dc3545", 
              color: "#fff",
              minWidth: "120px" 
            }}
            onClick={handleCancel}
          >
            ✖ Cancel
          </button>
        </div>
      </div>
    );
  }

  if (loading) return <Loader />;
  if (!bookingData) return <Loader />;

  return (

    <div className="gallery-container">

      {/* HEADER */}

      {showHeader && (
        <div className="gallery-header">
          <button onClick={handleBack} className="back-btn">
            ← Back
          </button>
          <button
            className="back-btn"
            onClick={handleViewPictures}
            disabled={uploading}
          >
            <FaEye style={{ marginRight: "6px" }} />
            View Gallery
          </button>
        </div>
      )}

      {/* BOOKING INFO */}
      {showBookingInfo && (
        <>
          <h2>{bookingData?.service_id?.serviceName}</h2>
          <p className="client-name">{bookingData?.client_id?.username}</p>
          <p>
            Event Location - {bookingData?.client_id?.city}, {bookingData?.client_id?.state}
          </p>
          <p>
            Event Date - {new Date(bookingData?.startDate).toLocaleDateString()}
          </p>
          <p>
            Photographer - {bookingData?.photographer_id?.basicInfo?.fullName}
          </p>
          <p>Booking ID - {bookingData?.veroaBookingId}</p>
        </>
      )}

      {/* UPLOAD BOX */}

      <div
        className="upload-box"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >

        <p>Drag and Drop photos/videos here</p>

        <span>OR</span>

        <div>

          <label className="upload-btn">

            Select Files

            <input
              type="file"
              hidden
              multiple
              accept="image/*,video/*"
              onChange={handleFileSelect}
            />

          </label>

          <label className="upload-btn">

            Select Folder

            <input
              type="file"
              hidden
              multiple
              webkitdirectory="true"
              onChange={handleFileSelect}
            />

          </label>

        </div>

        {files.length > 0 && (

          <button
            className="upload-btn"
            onClick={handleSubmitUpload}
            disabled={uploading}
          >
            {uploading ? "Uploading..." : `Upload Files (${files.length})`}
          </button>

        )}

      </div>

    </div>
  );
};

export default GalleryAddPhotos;

//========================================
// TRY RAHIL BHAI CODE

// import React, { useRef, useState } from 'react'
// import { S3UploadService } from './S3UploadServices';
// import { useParams } from 'react-router-dom';

// const GalleryUploadCard = () => {
// const [file, setFile] = useState(null);
//     const [uploading, setUploading] = useState(false);
//     const [progress, setProgress] = useState(0);
//     const [status, setStatus] = useState('');
//     const [retryInfo, setRetryInfo] = useState(null);
//     const [error, setError] = useState(null);
//     const uploadServiceRef = useRef(null);
//     const {bookingId} = useParams()
//     const handleFileChange = (e) => {
//         setFile(e.target.files[0]);
//         setError(null);
//         setRetryInfo(null);
//     };

//     const startUpload = async () => {
//         if (!file) return;

//         setUploading(true);
//         setError(null);
//         setStatus('Initializing upload...');

//         uploadServiceRef.current = new S3UploadService({
//             onProgress: ({ percent, part, totalParts }) => {
//                 setProgress(percent);
//                 setStatus(part ? `Uploading part ${part}/${totalParts}...` : 'Uploading...');
//                 setRetryInfo(null); // Clear retry info on success
//             },
//             onRetry: ({ attempt, maxRetries, nextRetryDelay, error }) => {
//                 setRetryInfo({ attempt, maxRetries, nextRetryDelay, error });
//                 setStatus(`Retry attempt ${attempt}/${maxRetries} in ${Math.round(nextRetryDelay/1000)}s...`);
//             },
//             onError: (err) => {
//                 setError(err.message);
//                 setUploading(false);
//                 setStatus('Upload failed');
//             },
//             onSuccess: ({ key }) => {
//                 setStatus('Upload successful!');
//                 setUploading(false);
//                 setFile(null);
//                 uploadServiceRef.current = null;
//             }
//         });

//         try {
//             await uploadServiceRef.current.upload(file, bookingId);
//         } catch (err) {
//             // Error handled via callback
//         }
//     };

//     const cancelUpload = () => {
//         if (uploadServiceRef.current) {
//             uploadServiceRef.current.cancel();
//             setUploading(false);
//             setStatus('Upload cancelled');
//         }
//     };

//     return (
//         <div className="p-6 bg-gray-900 text-white rounded-xl shadow-2xl space-y-4 max-w-md mx-auto border border-gray-700">
//             <h2 className="text-xl font-bold text-blue-400">Optimized S3 Upload</h2>
            
//             <input 
//                 type="file" 
//                 onChange={handleFileChange}
//                 disabled={uploading}
//                 className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-500 cursor-pointer"
//             />

//             {uploading && (
//                 <div className="space-y-2">
//                     <div className="flex justify-between text-xs">
//                         <span>{status}</span>
//                         <span>{progress}%</span>
//                     </div>
//                     <div className="w-full bg-gray-700 rounded-full h-2.5">
//                         <div className="bg-blue-500 h-2.5 rounded-full transition-all duration-300" style={{ width: `${progress}%` }}></div>
//                     </div>
//                 </div>
//             )}

//             {retryInfo && (
//                 <div className="p-3 bg-yellow-900/30 border border-yellow-700/50 rounded-lg text-sm space-y-1">
//                     <p className="text-yellow-400 font-medium flex items-center">
//                         <span className="animate-pulse mr-2">⚠️</span>
//                         Connection unstable. Retrying ({retryInfo.attempt}/{retryInfo.maxRetries})
//                     </p>
//                     <p className="text-xs text-yellow-500/80">Issue: {retryInfo.error}</p>
//                 </div>
//             )}

//             {error && (
//                 <div className="p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-sm text-red-400">
//                     <p className="font-bold">Error</p>
//                     <p>{error}</p>
//                     <button 
//                         onClick={startUpload}
//                         className="mt-2 text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-500"
//                     >
//                         Retry Manually
//                     </button>
//                 </div>
//             )}

//             <div className="flex space-x-3">
//                 {!uploading ? (
//                     <button 
//                         onClick={startUpload}
//                         disabled={!file}
//                         className={`flex-1 py-2 rounded-lg font-bold transition-all ${file ? 'bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-900/20' : 'bg-gray-700 cursor-not-allowed'}`}
//                     >
//                         Upload to S3
//                     </button>
//                 ) : (
//                     <button 
//                         onClick={cancelUpload}
//                         className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-bold"
//                     >
//                         Cancel
//                     </button>
//                 )}
//             </div>
//         </div>
//     );
// }

// export default GalleryUploadCard
//========================================









// import { useNavigate, useParams } from "react-router-dom";
// import { useEffect, useState } from "react";
// import "./GalleryUploadCard.css";
// import {
//   startUploadAPI,
//   uploadChunkAPI,
//   completeUploadAPI,
//   getBookingByIdAPI
// } from "../../../../utils/APIs/bookingsApis";
// import { toast } from "react-toastify";
// import Loader2 from "../../../../Loader/Loader2";
// import Loader from "../../../../Loader/Loader";

// const GalleryAddPhotos = () => {
//   const { bookingId } = useParams();

//   const [bookingData, setBookingData] = useState(null);
//   const [loading, setLoading] = useState(false);

//   const [files, setFiles] = useState([]);
//   const [uploading, setUploading] = useState(false);
//   const [uploadProgress, setUploadProgress] = useState(0);
//   const navigate=useNavigate()
//   const handleBack = () => navigate(-1);

//   /* ================= FETCH BOOKING ================= */

//   useEffect(() => {
//     const fetchBookingById = async () => {
//       try {
//         setLoading(true);

//         const res = await getBookingByIdAPI(bookingId);

//         if (res?.data?.success) {
//           setBookingData(res?.data?.data);
//         }

//       } catch {
//         toast.error("Failed to fetch booking details");
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (bookingId) fetchBookingById();

//   }, [bookingId]);

//   /* ================= FILE SELECT ================= */

//   const handleFileSelect = (e) => {
//     setFiles(Array.from(e.target.files));
//   };

//   const handleDrop = (e) => {
//     e.preventDefault();
//     setFiles(Array.from(e.dataTransfer.files));
//   };

//   const handleDragOver = (e) => {
//     e.preventDefault();
//   };

  
// const handleSubmitUpload = async () => {

//   if (!files.length) {
//     toast.error("Please select files");
//     return;
//   }

//   try {

//     setUploading(true);
//     setUploadProgress(0);

//     const CHUNK_SIZE = 20 * 1024 * 1024; //20MB

//     let totalPartsAllFiles = 0;
//     let uploadedParts = 0;

//     files.forEach((file) => {
//       totalPartsAllFiles += Math.ceil(file.size / CHUNK_SIZE);
//     });

//     for (const file of files) {

//       const relativePath = file.webkitRelativePath || file.name;

//       const startRes = await startUploadAPI({
//         fileName: file.name,
//         fileType: file.type,
//         relativePath, // ⭐ added
//         veroaBookingId: bookingData.veroaBookingId,
//       });

//       const { key, uploadId } = startRes.data;

//       const totalParts = Math.ceil(file.size / CHUNK_SIZE);
//       let parts = [];

//       for (let partNumber = 1; partNumber <= totalParts; partNumber++) {

//         const start = (partNumber - 1) * CHUNK_SIZE;
//         const end = Math.min(start + CHUNK_SIZE, file.size);

//         const chunkFile = file.slice(start, end);

//         const formData = new FormData();
//         formData.append("key", key);
//         formData.append("uploadId", uploadId);
//         formData.append("partNumber", partNumber.toString());
//         formData.append("chunk", chunkFile);

//         const chunkRes = await uploadChunkAPI(formData);

//         parts.push({
//           ETag: chunkRes?.data?.ETag || chunkRes?.data?.data?.ETag,
//           PartNumber: partNumber,
//         });

//         uploadedParts++;

//         const percent = Math.round(
//           (uploadedParts / totalPartsAllFiles) * 100
//         );

//         setUploadProgress(percent);
//       }

//       await completeUploadAPI({
//         key,
//         uploadId,
//         parts,
//         bookingid: bookingId,
//         clientId: bookingData.client_id?._id,
//         photographerId: bookingData.photographer_id,
//         veroaBookingId: bookingData.veroaBookingId,
//       });

//     }

//     toast.success("Files uploaded successfully 🎉");
//     setFiles([]);
//     setUploadProgress(0);

//   } catch {
//     toast.error("Upload failed. Please try again.");
//   } finally {
//     setUploading(false);
//   }
// };

//   /* ================= FILE VIEW ================= */
//   const handleViewPictures = (bookingData) =>{
//     navigate (`/bookings/view-gallery/${bookingId}/${bookingData?.photographer_id?._id}`)
//   }

//   /* ================= LOADER ================= */

//   if (loading) return <Loader />;
//   if (uploading) return <Loader2 percentage={uploadProgress} />;

//    const galleryBookingData = {
//     title: bookingData?.service_id?.serviceName,
//     coupleName: bookingData?.client_id?.username,
//     eventLocation: `${bookingData?.client_id?.city}, ${bookingData?.client_id?.state}`,
//     eventDate: `${new Date(bookingData?.startDate).toLocaleDateString("en-GB", {
//       day: "2-digit",
//       month: "long",
//       year: "numeric",
//     })} to ${new Date(bookingData?.endDate).toLocaleDateString("en-GB", {
//       day: "2-digit",
//       month: "long",
//       year: "numeric",
//     })}`,
//     photographer: bookingData?.photographer_id?.basicInfo?.fullName,
//     bookingId: bookingData?.veroaBookingId,
//   };


//   return (
//     <div
//       className="bg-white rounded-3"
//       style={{ background: "var(--color-bg-main)" }}
//     >

//       {/* HEADER */}
//       <div className="d-flex justify-content-between align-items-start mb-4">

//         <div>
//           <h2 className="wedding-title">{galleryBookingData.title}</h2>

//           <p className="couple-name">
//             {galleryBookingData.coupleName}
//           </p>

//           <div style={{
//             marginTop: "20px",
//             color: "var(--bs-secondary-color)",
//             fontSize: "14px",
//             lineHeight: "1.8",
//           }}>

//             <div>
//               Event Location - {galleryBookingData.eventLocation}
//             </div>

//             <div>
//               Event Date - {galleryBookingData.eventDate}
//             </div>

//             <div>
//               Photographer - {galleryBookingData.photographer}
//             </div>

//             <div>
//               Booking ID - {galleryBookingData.bookingId}
//             </div>

//           </div>
//         </div>

//         <button
//           className="upload-btn"
//         onClick={() => handleViewPictures(bookingData)}
//         >
//           👁 View Pictures
//         </button>

//       </div>

//       {/* UPLOAD BOX */}

//       <div
//         className="text-center py-5 mt-5 rounded"
//         style={{
//           background: "var(--white-color)",
//           border: "1px dashed rgba(74,51,38,0.2)",
//         }}
//         onDrop={handleDrop}
//         onDragOver={handleDragOver}
//       >

//         <p style={{ color: "var(--bs-secondary-color)" }}>
//           Drag & Drop photos/videos here
//         </p>

//         <p style={{ fontSize: "12px", color: "var(--bs-secondary-color)" }}>
//           OR
//         </p>
//         <div className="d-flex gap-2 justify-content-center">
//         <label className="upload-btn">

//           Select Files

//           <input
//             type="file"
//             hidden
//             multiple
//             accept="image/*,video/*"
//             onChange={handleFileSelect}
//           />

//         </label>
      
//          <label className="upload-btn">

//           Select Folder

//           <input
//             type="file"
//             hidden
//             multiple
//              webkitdirectory="true"
//             // accept="image/*,video/*"
//             onChange={handleFileSelect}
//           />

//         </label>


//          {files.length > 0 && (

//           <button
//             className="upload-btn"
//             // style={{ background: "var(--color-primary)", color: "#fff" }}
//             onClick={handleSubmitUpload}
//           >
//             Upload Files ({files.length})
//           </button>

//         )}
//         </div>

//         {/* FILE LIST */}

//         {/* {files.length > 0 && (

//           <div style={{ marginTop: "20px" }}>

//             {files.map((file) => (

//               <div key={file.name} style={{ marginBottom: "10px" }}>
//                 📦 {file.name}
//               </div>

//             ))}

//           </div>

//         )} */}

//       </div>

//       {/* BUTTONS */}

//       <div className="text-center mt-5">

//         <button
//           className="btn px-5 py-2 mx-5"
//           style={{ background: "var(--color-primary)", color: "#fff" }}
//           onClick={handleBack}
//         >
//           ← Back
//         </button>


//       </div>

//     </div>
//   );
// };

// export default GalleryAddPhotos;
